﻿import {  EmailSpeaker, Registration, RegistrationAffiliation } from '@/api/dtos';
import { AdminContext } from '@/contexts/AdminContext';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, Box, Grid, Paper, styled, Typography } from '@mui/material';
import { Checkbox, ChosenThemeActionContext, Form, FormSubmitButton, FormsFeatureContext, IValidationError, InputOnlyField, Select, Text, useFormLookups, useFormMetadata, useFormValidate } from '@ngt/forms';
import { IFormSubmit, useScopedField, ValidateOn, useFormActions } from '@ngt/forms-core';
import React, { FC, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminNavigation from '../AdminNavigation';
import { enqueueSnackbar } from 'notistack';
import CapCheck from '@/components/CapCheck';
import BoardCheck from '@/components/BoardCheck';
import useMemberDetails from '@/hooks/useMemberDetails';
import useDebouncedValue from '@/hooks/useDebouncedValue';

const SetInitialSpeaker: FC = () => {
    
    const {context: {setValue: setInitialSpeakerEmail}} = useScopedField<boolean>("initialSpeakerEmail", {value: true});

    useEffect(() => {
        setInitialSpeakerEmail(true);
    },[]);

    return null;
}


const MemberStaffCheckGrid = styled(Grid)(({theme}) => ({
    width: '100%',
    textAlign: 'left',
    display: 'block',
    paddingTop: 10,
    paddingBottom: 10,

    [theme.breakpoints.up('lg')]: {
        display: 'flex',
        gap: theme.spacing(3),
    },

}))

const GridMember = styled(Grid)(() => ({
    flex: '1 1 100%'

}))


const MemberDetailUpdate = () => {
    const {setValue} = useFormActions<Registration>();

    const {state: {value: firstName}} = useScopedField<string>('firstName', {value: true});
    const {state: {value: lastName}} = useScopedField<string>('lastName', {value: true});

    const debouncedFirstName = useDebouncedValue(firstName);
    const debouncedLastName = useDebouncedValue(lastName);

    const {data: memberDetails} = useMemberDetails(debouncedFirstName ?? undefined, debouncedLastName ?? undefined, { revalidateOnFocus: false, refreshWhenOffline: false, });

    React.useEffect(() => {
        if (!memberDetails || (!memberDetails.firstName && !memberDetails.email &&!memberDetails.title)) {
            return;
        }

        setValue((currentValue: Registration | undefined) => {

            if(currentValue?.email) {
                return currentValue;
            }

            if(currentValue?.title) {
                return currentValue;
            }
            
            return new Registration({
                ...currentValue,
                email: memberDetails.email,
                title: memberDetails.title
            });
        });

    }, [memberDetails, setValue]);

    return null;
}

const AdminAddSpeaker: FC = () => {
    const CustomRegistrationAffiliation: FC = () => {
        return (
            <Grid container spacing={4} pt={5} pl={4}>
                <Grid item xs={12} sm={6} md={3}>
                    <InputOnlyField
                        label="Affiliation"
                        propertyPath={"registrationAffiliation"}
                        component={Select}
                        FormControlProps={{
                            variant: "standard"
                        }}
                        sx={{margin:"normal"}}
                    />
                </Grid>
            </Grid>
        );
    };

    const navigate = useNavigate();
    
    const setChosenTheme = useContext(ChosenThemeActionContext);

    const formMetadata = useFormMetadata(1);

    const {data:lookups} = useFormLookups(formMetadata, { revalidateOnFocus: false, refreshWhenOffline: false, })

    const validate: any = useFormValidate(formMetadata)

    useEffect(() => {
        setChosenTheme('light');
    }, [])

    const {serviceStackClient} = useContext(FormsFeatureContext);

    const [registration] = React.useState<Registration | undefined>();

    const [, setShouldDisable] = React.useState<boolean>(false);
    
    const RegistrationAffiliationLogic = () => {

        const {state: {value: registrationAffiliation}} = useScopedField<RegistrationAffiliation>("registrationAffiliation", {value: true});

        useEffect(() => {
            if (registrationAffiliation !== RegistrationAffiliation.GuestSpeaker) {
                setShouldDisable(true);
            }
            else {
                setShouldDisable(false);
            }
        }, [registrationAffiliation]);

        return null;
    }

    const onSubmit:IFormSubmit<Registration, IValidationError> = async (formState) => {
        if (!formState.value) {
            return;
        }

        let registration : Registration = new Registration({...formState.value});

        registration.speakerPresentations?.forEach(sp => {
            sp.registrationId = registration.id;
        });

        try {
            await serviceStackClient.post(new EmailSpeaker({ registration }));
            navigate('/admin/custom-registrations', { 
                state: { 
                    successMessage: 'Registration email sent',
                    shouldRefresh: true 
                } 
            });           
            
        } catch (err: any) {
            enqueueSnackbar(err?.responseStatus?.message ?? 'Failed to send registration email', { variant: 'error' });
        }
    }

    
    return (
        <>
            <AdminNavigation/>
            <Box sx={{paddingLeft: '100px', paddingRight: '100px', paddingTop:'30px', paddingBottom:'30px'}}>
                <Paper elevation={2} sx={{padding:'40px', pb: 10}}>
                    <Grid container spacing={1}>
                        <Grid 
                            item 
                            xs={12}
                            sm={12}
                            md={12}
                            lg={6}
                            xl={9}
                        >
                            <Typography fontFamily='Roboto' color='primary' fontSize='30pt'>Custom Registration</Typography>
                        </Grid>

                    </Grid>
                    <Form
                        initialValue={registration ?? null}
                        debug={import.meta.env.DEV}
                        lookups={lookups}
                        onValidate={validate}
                        validateOn={ValidateOn.onChange}
                        onSubmit={onSubmit}
                    >
                        <SetInitialSpeaker/>

                        <MemberDetailUpdate/>
                        <RegistrationAffiliationLogic />

                        {/* <FormDebugPopupButton /> */}
                        <AdminContext.Provider value={true}>
                        

                        <Grid 
                            item
                            xs={12}
                            sx={{
                                marginTop:"20px",
                            }}
                        >

                            <Grid  
                                item
                                xs={12}
                                sx={{
                                    marginBottom:"20px",
                                }}
                                
                            >
                                <Alert
                                    severity="info"
                                    icon={<FontAwesomeIcon fixedWidth icon={faExclamationCircle} />}
                                >
                                    Once a Delegate is added, they will be emailed with a link to complete their registration. 
                                </Alert>

                                <MemberStaffCheckGrid style={{paddingTop: 50}}>
                                    <GridMember>
                                        <CapCheck speakerSection={true} />
                                    </GridMember>
                                        <GridMember>
                                        <BoardCheck speakerSection={true} />
                                    </GridMember>
                                </MemberStaffCheckGrid>
                                
                            </Grid>

                            <Grid 
                                container
                                spacing={4}
                            >

                                
                                <Grid  
                                    item
                                    xs={12}
                                    sm={6}
                                    md={3}
                                >
                                    <InputOnlyField
                                        label="Title"
                                        propertyPath="title"
                                        component={Select}
                                        sx={{margin:"normal"}}
                                    />
                                </Grid>
                                <Grid  
                                    item
                                    xs={12}
                                    sm={6}
                                    md={3}
                                >
                                    <InputOnlyField
                                        label="First Name"
                                        propertyPath="firstName"
                                        component={Text}
                                        sx={{margin:"normal"}}
                                    />
                                </Grid>
                                <Grid  
                                    item
                                    xs={12}
                                    sm={6}
                                    md={3}
                                >
                                    <InputOnlyField
                                        label="Last Name"
                                        propertyPath="lastName"
                                        component={Text}
                                        sx={{margin:"normal"}}
                                    />
                                </Grid>
                                <Grid  
                                    item
                                    xs={12}
                                    sm={6}
                                    md={3}
                                >
                                    <InputOnlyField
                                        label="Email"
                                        propertyPath="email"
                                        component={Text}
                                        sx={{margin:"normal"}}
                                    />
                                </Grid>
                                <CustomRegistrationAffiliation/>
                                <Grid  
                                    item
                                    xs={12}
                                    sm={6}
                                    md={3}
                                >
                                    <InputOnlyField
                                        label="T&EC Speaker"
                                        propertyPath="isTAndECSpeaker"
                                        component={Checkbox}
                                        sx={{margin:"normal"}}
                                    />

                                </Grid>
                                <Grid  
                                    item
                                    xs={12}
                                    sm={6}
                                    md={3}
                                >
                                    <InputOnlyField
                                        label="Trial Coordinator"
                                        propertyPath="trialCoordinator"
                                        component={Checkbox}
                                        sx={{margin:"normal"}}  
                                    />
                                </Grid>
                                <Grid  
                                    item
                                    xs={12}
                                    sm={6}
                                    md={3}
                                >
                                    <InputOnlyField
                                        label="Registrar"
                                        propertyPath="registrar"
                                        component={Checkbox}
                                        sx={{margin:"normal"}}
                                    />
                                </Grid>
                                <Grid  
                                    item
                                    xs={12}
                                    sm={6}
                                    md={3}
                                >
                                    <InputOnlyField
                                        label="Student"
                                        propertyPath="student"
                                        component={Checkbox}
                                        sx={{margin:"normal"}}
                                    />
                                </Grid>
                                
                            </Grid>
                        </Grid>
                        

                        <FormSubmitButton variant="contained"
                            sx={{
                                float: "right",
                                marginTop:"20px",
                                marginBottom: "20px"
                            }}
                            >
                            Submit
                        </FormSubmitButton>                  

                        </AdminContext.Provider>
                    </Form>
                </Paper>
            </Box>
        </>
    )
}

export default AdminAddSpeaker;
